import { Line } from '@react-three/drei'
import { useMemo } from 'react'
import { multiplicar, paraArray, somar, type Vetor3 } from '../../dominio/geometria/vetor3'
import { pontosDoArco, type RotaExtracao } from '../../dominio/didatico/rotasExtracao'
import { CORES_SEMANTICAS } from '../../dominio/didatico/tokens'
import { Rotulo3D } from '../componentes/Rotulo3D'

interface Props {
  rota: RotaExtracao
  centro: Vetor3
  raio?: number
  mostrarValor?: boolean
  espessura?: number
  /**
   * Cor do arco. O padrão é o roxo do eixo anatômico, que é o que a rota
   * comparada continua usando: só a rota EM ANÁLISE recebe a cor da faixa de
   * giro, senão as duas competem e nenhuma informa.
   */
  cor?: string
}

/**
 * Arco que mostra a abertura do ângulo de retirada.
 *
 * Os pontos vêm de `pontosDoArco`, que usa o eixo da coluna e o eixo sagital
 * devolvidos pelo cálculo. Arco e número não podem divergir porque saem da
 * mesma fonte.
 */
export function ArcoAngulo({
  rota,
  centro,
  raio = 0.62,
  mostrarValor = true,
  espessura = 4,
  cor = CORES_SEMANTICAS.eixoAnatomico,
}: Props) {
  const pontos = useMemo(() => pontosDoArco(rota, centro, raio, 44), [rota, centro, raio])
  const graus = rota.resultado.anguloGraus
  const ehAxialOuZero = rota.resultado.rotaAxial || (graus != null && graus <= 0.5) || rota.anguloNominal === 0

  // Se for rota axial ou 0°, não há arco curvo (pontos.length < 2), mas há direção de retirada (linha reta) e rótulo de 0°
  if (ehAxialOuZero) {
    const direcaoReferencia = rota.giro?.referencia
    if (!direcaoReferencia) return null
    const pontoFim = somar(centro, multiplicar(direcaoReferencia, raio))

    return (
      <group>
        <Line
          points={[paraArray(centro), paraArray(pontoFim)]}
          color={cor}
          lineWidth={2}
          dashed
          dashSize={0.07}
          gapSize={0.05}
        />
        {mostrarValor && (
          <Rotulo3D
            posicao={somar(pontoFim, { x: 0, y: 0.16, z: 0 })}
            texto={rota.resultado.rotaAxial ? '0° (axial)' : `${rota.anguloConvencional ?? 0}°`}
            variante="eixo"
            fator={5}
            sempreVisivel
          />
        )}
      </group>
    )
  }

  if (pontos.length < 2 || graus == null) return null

  const meio = pontos[Math.floor(pontos.length / 2)]!
  const raios = [pontos[0]!, pontos[pontos.length - 1]!]

  return (
    <group>
      {graus > 0.5 && <Line points={pontos.map(paraArray)} color={cor} lineWidth={espessura} />}
      {/* os dois raios do ângulo: referência sagital e direção da rota */}
      {raios.map((p, i) => (
        <Line
          key={i}
          points={[paraArray(centro), paraArray(p)]}
          color={cor}
          lineWidth={2}
          dashed
          dashSize={0.07}
          gapSize={0.05}
        />
      ))}
      {mostrarValor && (
        <Rotulo3D
          posicao={somar(meio, { x: 0, y: 0.16, z: 0 })}
          /* o rótulo da cena mostra o valor de trabalho, o mesmo do painel;
             o calculado fica no painel, onde há espaço para explicá-lo */
          texto={`${rota.anguloConvencional ?? graus.toFixed(0)}°`}
          variante="eixo"
          fator={5}
          sempreVisivel
        />
      )}
    </group>
  )
}
